"use client";

/**
 * "Do you need dedicated PCN management software?" diagnostic (Phase 2).
 *
 * A self-check the visitor fills in. PRIVACY: selections live only in the browser
 * and are NEVER sent anywhere — analytics receives only an anonymous
 * `diagnostic_started` signal and a `diagnostic_completed` with the aggregate
 * result band ("low"/"some"/"high"), never which statements were ticked. No email
 * is required.
 *
 * Accessibility: native checkboxes inside a <fieldset>/<legend> (full keyboard +
 * screen-reader support), and the result is an aria-live status region so it's
 * announced as the selection changes. No motion animations (reduced-motion safe).
 */
import * as React from "react";
import posthog from "posthog-js";
import { ConversionCta, type CtaContext } from "./cta";
import type { AnalyticsEvent, SafeEventProps } from "@/lib/analytics/events";
import type { GuideContent } from "@/lib/seo/content/types";

function capture(event: AnalyticsEvent, props: SafeEventProps) {
  try {
    posthog.capture(event, props);
  } catch {
    /* analytics must never break the page */
  }
}

function band(count: number): "low" | "some" | "high" {
  if (count >= 4) return "high";
  if (count >= 2) return "some";
  return "low";
}

export function DoYouNeedItDiagnostic({
  content,
  context,
}: {
  content: NonNullable<GuideContent["diagnostic"]>;
  context: CtaContext;
}) {
  const [selected, setSelected] = React.useState<Set<number>>(() => new Set());
  const [touched, setTouched] = React.useState(false);
  const startedRef = React.useRef(false);
  const lastBandRef = React.useRef<string>("");

  const count = selected.size;
  const b = band(count);
  const resultText =
    b === "high"
      ? content.results.high
      : b === "some"
        ? content.results.some
        : content.results.low;

  function toggle(i: number) {
    if (!touched) setTouched(true);
    if (!startedRef.current) {
      startedRef.current = true;
      capture("diagnostic_started", {
        source_page: context.source_page,
        page_type: context.page_type,
      });
    }
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  }

  // Report the result band only (never the selected statements) when it changes.
  React.useEffect(() => {
    if (!touched) return;
    const nb = band(selected.size);
    if (nb !== lastBandRef.current) {
      lastBandRef.current = nb;
      capture("diagnostic_completed", {
        source_page: context.source_page,
        page_type: context.page_type,
        result: nb,
      });
    }
  }, [selected, touched, context.source_page, context.page_type]);

  return (
    <section id="diagnostic" className="scroll-mt-20 border-b rule bg-paper-2">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-8 py-16">
        <div className="font-mono text-[11px] uppercase tracking-wider ink-3 mb-3">
          § Self-check
        </div>
        <h2 className="font-display font-semibold text-[26px] lg:text-[32px] tracking-[-0.025em] leading-[1.08] max-w-[28ch]">
          {content.heading}
        </h2>
        {content.intro && (
          <p className="mt-3 text-[15px] ink-2 leading-relaxed max-w-[80ch]">
            {content.intro}
          </p>
        )}

        <fieldset className="mt-6 border rule bg-paper max-w-[80ch]">
          <legend className="sr-only">{content.heading}</legend>
          <ul className="divide-y divide-[color:var(--rule)]">
            {content.items.map((item, i) => (
              <li key={i}>
                <label className="flex items-start gap-3 px-5 py-3.5 cursor-pointer hover:bg-paper-2 transition-colors text-[14px] ink-2 leading-relaxed">
                  <input
                    type="checkbox"
                    checked={selected.has(i)}
                    onChange={() => toggle(i)}
                    className="mt-0.5 h-4 w-4 shrink-0 accent-[color:var(--ink)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--shark-accent)]"
                  />
                  <span>{item}</span>
                </label>
              </li>
            ))}
          </ul>
        </fieldset>

        <p className="mt-3 font-mono text-[11px] ink-3">
          {count} of {content.items.length} selected
        </p>

        {touched && (
          <div
            role="status"
            aria-live="polite"
            className="mt-4 border rule bg-paper p-5 max-w-[80ch]"
          >
            <p className="text-[14.5px] ink-2 leading-relaxed">{resultText}</p>
            <div className="mt-4">
              <ConversionCta cta={content.cta} context={context} variant="primary" />
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
